import { useContext } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { RopCard1103ImpactoNaMetaTaticoAggregated, RopCard1109PerformanceByAssetTaticoAggregated, TacticalInterval } from "../../types";
import RadarIcon from '@mui/icons-material/Radar';
import ImpactoNaMetaTacticalChart from "./ImpactoNaMetaTacticalChart";
import PerformanceCard from "./PerformanceCard";
import DesvioPeriodoCard from "./DesvioPeriodoCard";
import DesviosCriticosCard from "./DesviosCriticosCard";
import DetalhamentoPorAtivoTable from "./DetalhamentoPorAtivoTable";
import ProgressoAnualCard from "./ProgressoAnualCard";
import SobreCalculosCard from "./SobreCalculosCard";

export type ImpactoNaMetaTacticalProps = {
    data: RopCard1103ImpactoNaMetaTaticoAggregated;
    performanceByAsset: RopCard1109PerformanceByAssetTaticoAggregated;
    interval?: TacticalInterval;
    criticalDeviationsTotal?: number;
    selectedMonth?: number;
    selectedYear?: number;
    customStartDate?: string;
    customEndDate?: string;
};

const MONTH_NAMES_PT = [
    "janeiro", "fevereiro", "março", "abril", "maio",
    "junho", "julho", "agosto", "setembro", "outubro",
    "novembro", "dezembro"
];

function getIndividualMonthTargets(
    monthlyData: Record<string, { target: number }>
): Record<number, number> {
    const sorted = MONTH_NAMES_PT
        .map((name, index) => ({ index, cumulative: monthlyData[name]?.target ?? 0 }))
        .sort((a, b) => a.index - b.index);

    const result: Record<number, number> = {};
    for (let i = 0; i < sorted.length; i++) {
        const prev = i > 0 ? sorted[i - 1].cumulative : 0;
        result[sorted[i].index] = sorted[i].cumulative - prev;
    }
    return result;
}

function calculatePeriodTarget(
    monthlyData: Record<string, { target: number }>,
    interval?: TacticalInterval,
    selectedMonth?: number,
    selectedYear?: number,
    customStartDate?: string,
    customEndDate?: string
): number {
    const individualTargets = getIndividualMonthTargets(monthlyData);

    const now = new Date();
    const refMonth = selectedMonth ?? now.getMonth();
    const refYear = selectedYear ?? now.getFullYear();
    const daysInMonth = new Date(refYear, refMonth + 1, 0).getDate();
    const monthTarget = individualTargets[refMonth] ?? 0;
    const dailyTarget = daysInMonth > 0 ? monthTarget / daysInMonth : 0;

    switch (interval) {
        case TacticalInterval.Daily:
            return dailyTarget;
        case TacticalInterval.Weekly:
            return dailyTarget * 7;
        case TacticalInterval.Monthly:
            return monthTarget;
        case TacticalInterval.Custom: {
            if (!customStartDate || !customEndDate) return 0;
            let total = 0;
            const start = new Date(customStartDate + "T12:00:00");
            const end = new Date(customEndDate + "T12:00:00");
            const current = new Date(start);
            while (current <= end) {
                const m = current.getMonth();
                const y = current.getFullYear();
                const mTarget = individualTargets[m] ?? 0;
                const daysInM = new Date(y, m + 1, 0).getDate();
                total += daysInM > 0 ? mTarget / daysInM : 0;
                current.setDate(current.getDate() + 1);
            }
            return total;
        }
        default:
            return 0;
    }
}

function getPeriodTitle(interval?: TacticalInterval, selectedMonth?: number, selectedYear?: number): string {
    const monthNames = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];

    switch (interval) {
        case TacticalInterval.Daily:
            return "Geração Diária";
        case TacticalInterval.Weekly:
            return "Geração Semanal";
        case TacticalInterval.Monthly:
            if (selectedMonth !== undefined && selectedYear !== undefined) {
                return `Geração de ${monthNames[selectedMonth]}/${selectedYear}`;
            }
            return "Geração Mensal";
        case TacticalInterval.Custom:
            return "Geração do Período";
        default:
            return "Geração do Período";
    }
}

export default function ImpactoNaMetaTactical({
    data,
    interval,
    performanceByAsset: _performanceByAsset,
    criticalDeviationsTotal,
    selectedMonth,
    selectedYear,
    customStartDate,
    customEndDate
}: ImpactoNaMetaTacticalProps) {
    const { theme } = useContext(MainContext);

    const periodTarget = calculatePeriodTarget(
        data.monthlyData, interval, selectedMonth, selectedYear, customStartDate, customEndDate
    );

    const deviation = data.periodData.generatedEnergy - periodTarget;
    const deviationPercentage = periodTarget > 0
        ? (data.periodData.generatedEnergy / periodTarget) * 100
        : 0;

    // Meta Anual = meta acumulada de dezembro
    const metaAnualTotal = data.monthlyData['dezembro']?.target ?? 0;

    // Real YTD = valor acumulado do último mês disponível (mês atual)
    const now = new Date();
    const currentMonthName = MONTH_NAMES_PT[now.getMonth()];
    const realYTD = data.monthlyData[currentMonthName]?.generatedEnergy ?? 0;

    // Meta YTD Proporcional = meta esperada até hoje (soma dos Egrid dos meses até o atual)
    const metaYTD = data.monthlyData[currentMonthName]?.target ?? 0;

    const performanceCardData = [
        {
            title: "Real",
            value: data.periodData.generatedEnergy.toFixed(1),
            suffix: "MWh",
        },
        {
            title: "Meta",
            value: periodTarget.toFixed(1),
            suffix: "MWh",
        },
    ];

    const acumuladoCardData = [
        {
            title: "Real YTD",
            value: realYTD.toFixed(1),
            suffix: "MWh",
            color: "info"
        },
        {
            title: "Previsto YTD",
            value: metaYTD.toFixed(1),
            suffix: "MWh",
            color: "info"
        },
        {
            title: "Meta Anual",
            value: metaAnualTotal.toFixed(1),
            suffix: "MWh",
        },
    ];

    const performanceTitle = getPeriodTitle(interval, selectedMonth, selectedYear);

    const getTitle = () => {
        switch (interval) {
            case TacticalInterval.Daily:
                return "Impacto Diário na Meta";
            case TacticalInterval.Weekly:
                return "Impacto Semanal na Meta";
            case TacticalInterval.Monthly:
                return "Impacto Mensal na Meta";
            case TacticalInterval.Custom:
                return "Impacto no Período";
            default:
                return "Impacto no Período";
        }
    }

    const getSubtitle = () => {
        switch (interval) {
            case TacticalInterval.Daily:
                return "Geração diária e impacto acumulado no ano";
            case TacticalInterval.Weekly:
                return "Geração semanal e impacto acumulado no ano";
            case TacticalInterval.Monthly:
                return "Geração mensal e impacto acumulado no ano";
            case TacticalInterval.Custom:
                return "Geração no período selecionado";
            default:
                return "Geração no período selecionado";
        }
    }

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                padding: "16px",
                backgroundColor: theme.cardBoxBackgroudColor,
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box sx={{ display: "flex", color: "#B980F3" }}>
                    <RadarIcon style={{ fontSize: "20px" }} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>
                        {getTitle()}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "400", color: theme?.secondaryTextColor || "#6B7280" }}>
                        {getSubtitle()}
                    </Typography>
                </Box>
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                        }}
                    >
                        <Box
                            sx={{
                                backgroundColor: theme.backgroundColor,
                                borderRadius: "8px"
                            }}
                        >
                            <Typography sx={{ fontSize: "14px", fontWeight: "500", marginBottom: "12px", textAlign: "left", paddingY: "12px" }}>
                                {performanceTitle}
                            </Typography>
                            <Grid container spacing={1.5}>
                                {performanceCardData.map((card) => (
                                    <Grid item xs={6} key={card.title}>
                                        <PerformanceCard
                                            title={card.title}
                                            value={card.value}
                                            suffix={card.suffix}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                            <DesvioPeriodoCard
                                deviation={deviation}
                                deviationPercentage={deviationPercentage}
                            />
                        </Box>

                        <DesviosCriticosCard value={criticalDeviationsTotal} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography sx={{ fontSize: "14px", fontWeight: "500", marginBottom: "12px", textAlign: "left", paddingY: "12px" }}>
                        Geração acumulada ano
                    </Typography>
                    <ImpactoNaMetaTacticalChart data={data} />
                    <Grid container spacing={1.5}>
                        {acumuladoCardData.map((card) => (
                            <Grid item xs={4} key={card.title}>
                                <PerformanceCard
                                    title={card.title}
                                    value={card.value}
                                    suffix={card.suffix}
                                    color={card.color}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <Grid item xs={12}>
                        <ProgressoAnualCard
                            metaAnual={metaAnualTotal}
                            realYTD={realYTD}
                            metaYTD={metaYTD}
                        />
                    </Grid>
                </Grid>

                <Grid item xs={12} sx={{ maxHeight: "450px", overflow: "auto" }}>
                    {data.clusterData && data.clusterData.length > 0 && (
                        <DetalhamentoPorAtivoTable
                            clusters={data.clusterData}
                            loading={false}
                        />
                    )}
                </Grid>
                <Grid item xs={12}>
                    <SobreCalculosCard />
                </Grid>
            </Grid>
        </Box>
    );
}